import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import moment from "moment";
import { CustomButton, CustomMRTTable } from "../../../components";
import AxiosInstance from "../../../services/Api";
import styles from "./PlansPricing.module.css";

const LIST_PATH = "/dashboard/masters/plans-pricing";
const LIST_PLAN_API = "/saas/planList";

const STATUS_STYLES = {
  Active: { backgroundColor: "#00A63E", color: "#fff" },
  Upcoming: { backgroundColor: "#039DD9", color: "#fff" },
  Archived: { backgroundColor: "#E7000B", color: "#fff" },
};

const StatusBadge = ({ status }) => (
  <span
    style={{
      display: "inline-block",
      padding: "4px 14px",
      borderRadius: "6px",
      fontSize: "13px",
      fontWeight: 600,
      ...(STATUS_STYLES[status] || { backgroundColor: "#9e9e9e", color: "#fff" }),
    }}
  >
    {status}
  </span>
);

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "—";
};

// Active plans list first; everything else keeps the order the API returned.
// Array.prototype.sort is stable, so equal ranks are left untouched.
const activeFirst = (a, b) => {
  const rank = (row) =>
    String(row.status || "").trim().toLowerCase() === "active" ? 0 : 1;
  return rank(a) - rank(b);
};

// ─────────────────────────────────────────────────────────────────────────────

const PlansPricingList = () => {
  const navigate = useNavigate();

  const [plans, setPlans] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await AxiosInstance.post(LIST_PLAN_API, {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        status: "",
      });

      if (response.data?.status) {
        // ✅ data is a direct array
        const list = Array.isArray(response.data.data) ? response.data.data : [];

        // ✅ pagination is top-level in response, NOT inside data
        const apiTotal = response.data.pagination?.total ?? list.length;
        const totalPages = response.data.pagination?.total_pages ?? 1;
        // If only 1 page, trust actual list length over potentially stale API total
        const trueTotal = totalPages === 1 ? list.length : apiTotal;

        // ✅ Map raw API rows to display rows
        const mapped = list.map((item) => {

          return {
            id: item.plan_id,
            plan_name: item.plan_name,
            plan_type: item.plan_type,
            school_count: item.school_count,
            created_date: item.created_date,
            status: item.status,
          };
        });

        setPlans(mapped.sort(activeFirst));
        setTotalRecords(trueTotal);
      } else {
        setPlans([]);
        setTotalRecords(0);
        throw new Error(response.data?.message || "Failed to fetch plans");
      }
    } catch (err) {
      console.error("Error fetching plans:", err);
      setError(err.message || "An error occurred while fetching plans");
      setPlans([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const columns = useMemo(
    () => [
      {
        id: "s_no",
        header: "S.No",
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) =>
          pagination.pageIndex * pagination.pageSize + row.index + 1,
      },
      {
        accessorKey: "plan_name",
        header: "Plan Name",
        size: 200,
        enableColumnFilter: false,
      },
      {
        accessorKey: "plan_type",
        header: "Plan Type",
        size: 200,
        enableColumnFilter: false,
      },
      {
        // school_count from API (number of schools using this plan)
        accessorKey: "school_count",
        header: "School Count",
        size: 140,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const val = cell.getValue();
          return val != null ? Number(val).toLocaleString("en-IN") : "—";
        },
      },
      {
        accessorKey: "created_date",
        header: "Created Date",
        size: 140,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ cell }) => <StatusBadge status={cell.getValue()} />,
      },
      {
        id: "actions",
        header: "Action",
        size: 80,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Tooltip title="View" arrow>
            <IconButton
              size="small"
              className={styles.iconActionBtn}
              style={{
                background: "#F5821F",
                color: "#fff",
                borderRadius: "6px",
                padding: "6px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`${LIST_PATH}/view/${row.original.id}`);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [pagination.pageIndex, pagination.pageSize, navigate],
  );

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Plans &amp; Pricing</h1>
        </div>
        <div className={styles.headerActions}>
          <CustomButton
            variant="primary"
            fullWidth={false}
            className={styles.btnOrange}
            onClick={() => navigate(`${LIST_PATH}/create`)}
          >
            Create Plan
          </CustomButton>
          <CustomButton
            variant="primary"
            fullWidth={false}
            className={styles.btnOrange}
            onClick={() => navigate("/dashboard/masters/slab-configuration")}
          >
            Slab Configuration
          </CustomButton>
          <CustomButton
            variant="primary"
            fullWidth={false}
            className={styles.btnOrange}
            onClick={() => navigate("/dashboard/masters/addon-configuration")}
          >
             Add-on Configuration
          </CustomButton>
        </div>
      </div>

      {error && <p style={{ color: "#E23B3B", margin: "12px 0" }}>{error}</p>}

      <div className={styles.card}>
        <h2 className={styles.sectionTitle}>Plans</h2>
        <CustomMRTTable
          columns={columns}
          data={plans}
          rowCount={totalRecords}
          isLoading={loading}
          manualPagination
          manualFiltering={false}
          pagination={pagination}
          onPaginationChange={setPagination}
          enablePagination
          enableSorting
          enableSearch={false}
          enableColumnFilters={false}
          enableColumnVisibility={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableHiding={false}
          enableColumnActions={false}
          enableStickyHeader
        />
      </div>
    </div>
  );
};

export default PlansPricingList;
